import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { IonicApp, IonicErrorHandler, IonicModule } from 'ionic-angular';

import { MyApp } from './app.component';

// Pages
import { DashboardPage } from '../pages/dashboard/dashboard';
import { InstructorsPage } from '../pages/instructors/instructors';
import { InstructorCandidatesPage } from '../pages/instructors/instructor-candidates';
import { InstructorAddEditPage } from '../pages/instructors/instructor-add-edit';
import { ReportsPage } from '../pages/reports/reports';
import { UsersPage } from '../pages/users/users';
import { UserAccountPage } from '../pages/user-account/user-account';
import { LoginPage } from '../pages/login/login';
import { RecertificationViewPage } from '../pages/recertifications/recertification-view';
import { RecertificationsPage } from '../pages/recertifications/recertifications';

// Reports
import { ReportCertificationStatusDateRangePage } from '../pages/reports/report.certifications-date-range';
import { ReportEmailListPage } from '../pages/reports/report.email-list';
import { ReportInstructorsEmployerPage } from '../pages/reports/report.instructors-employer';
import { ReportInstructorsLocationPage } from '../pages/reports/report.instructors-location'; 
import { ReportActiveProviderPage } from '../pages/reports/report.active-provider';
import { ReportUncertifiedCandidatePage } from '../pages/reports/report.uncertified-candidate';

// Popovers
import { UserAccountPopoverPage } from '../components/popovers/user-account.popover';
import { ListActionsPopoverPage } from '../components/popovers/list-actions.popover';
import { CertifiedHoursPopoverPage } from '../components/popovers/certified-hours.popover';

// Providers
import { TokenInterceptor } from '../providers/http/token.interceptor';
import { InstructorsService } from '../providers/instructors/instructors-service';
import { UsersService } from '../providers/users/users-service';
import { ProvidersService } from '../providers/providers/providers-service';
import { HelperService } from '../providers/helpers/helper-service';
import { HelperCsvService } from '../providers/helpers/helper-csv-service';
import { LoginService } from '../providers/login/login-service';
import { ImageService } from '../providers/images/image-service';
import { RecertificationService } from '../providers/instructors/recertification-service';
import { PaymentService } from '../providers/payment/payment-service';
import { NotificationsService } from '../providers/notifications/notifications-service';
import { FilesService } from '../providers/files/files-service';

// Components
import { CustomHeaderComponent } from '../components/header-custom.component';
import { CustomDatePickerComponent } from '../components/date-picker/date-picker.component';
import { DashboardCountComponent } from '../pages/dashboard/components/dashboard-count.component';
import { DashboardCountRecertificationComponent } from '../pages/dashboard/components/dashboard-count-recertification.component';
import { FileComponent } from '../components/file/file.component'; 
import { DashboardNotificationsComponent } from '../pages/dashboard/components/dashboard-notifications.component'


// Modals
import { IncidentModalPage } from '../components/modals/incident.modal';
import { CertificationModalPage } from '../components/modals/certification.modal';
import { EmployerModalPage } from '../components/modals/employer.modal';
import { CourseModalPage } from '../components/modals/course.modal';
import { EducationModalPage } from '../components/modals/education.modal';
import { ProviderInfoModalPage } from '../components/modals/provider-info.modal';
import { InstructorPaymentModalPage } from '../components/modals/instructor-payment.modal';

// Modules
import { FormsModule } from '@angular/forms';
import { ChartsModule } from 'ng2-charts';
import { LinkyModule } from 'angular-linky';
import { RichTextComponentModule } from '../components/rich-text/rich-text.module';
import { UploadComponentModule } from '../components/file-upload/file-upload.module';

// Pipes
import { NewlinePipe } from '../pipes/common-pipes';
import { ReplacePipe } from '../pipes/common-pipes';
import { FileextensionPipe } from '../pipes/common-pipes';
import { MomentFromNowPipe } from '../pipes/common-pipes';
import { MomentConvertMySqlPipe } from '../pipes/common-pipes';
import { FilePurposePipe } from '../pipes/common-pipes';

// Directives
import { InputMask } from '../directives/input-mask.directive'; 

@NgModule({
  declarations: [
    MyApp,
    DashboardPage,
    InstructorsPage,
    InstructorAddEditPage,
    InstructorCandidatesPage,

    ReportsPage,
    ReportCertificationStatusDateRangePage,
    ReportEmailListPage,
    ReportInstructorsEmployerPage,
    ReportInstructorsLocationPage,
    RecertificationViewPage,
    RecertificationsPage,
    ReportActiveProviderPage,
    ReportUncertifiedCandidatePage,

    UsersPage,
    UserAccountPage,
    LoginPage,

    CustomHeaderComponent,
    CustomDatePickerComponent,
    DashboardCountComponent,
    DashboardNotificationsComponent,
    DashboardCountRecertificationComponent,
    FileComponent,

    IncidentModalPage,
    CertificationModalPage,
    EmployerModalPage,
    CourseModalPage,
    EducationModalPage,
    ProviderInfoModalPage,
    InstructorPaymentModalPage,

    NewlinePipe,
    ReplacePipe,
    FileextensionPipe,
    MomentFromNowPipe,
    MomentConvertMySqlPipe,
    FilePurposePipe,

    UserAccountPopoverPage,
    ListActionsPopoverPage,
    CertifiedHoursPopoverPage,

    InputMask
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    ChartsModule,
    FormsModule,
    LinkyModule,
    UploadComponentModule,
    RichTextComponentModule,
    
    IonicModule.forRoot(MyApp, {
      backButtonText: '',
      backButtonIcon: 'arrow-back',
      platforms: {
        ios: {
          scrollAssist: false,
          autoFocusAssist: false,
          statusbarPadding: true
        }
      }
    }),
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    MyApp,
    DashboardPage,
    InstructorsPage,
    InstructorAddEditPage,
    InstructorCandidatesPage,

    ReportsPage,
    ReportCertificationStatusDateRangePage,
    ReportEmailListPage,
    ReportInstructorsEmployerPage,
    ReportInstructorsLocationPage,
    RecertificationViewPage,
    RecertificationsPage,
    ReportActiveProviderPage,
    ReportUncertifiedCandidatePage,
    
    UsersPage,
    UserAccountPage,
    LoginPage,

    IncidentModalPage,
    CertificationModalPage,
    EmployerModalPage,
    CourseModalPage,
    EducationModalPage,
    ProviderInfoModalPage,
    InstructorPaymentModalPage,

    UserAccountPopoverPage,
    ListActionsPopoverPage,
    CertifiedHoursPopoverPage
  ],
  providers: [
    InstructorsService,
    UsersService,
    ProvidersService,
    HelperService,
    HelperCsvService,
    LoginService,
    ImageService,
    RecertificationService,
    PaymentService,
    NotificationsService,
    FilesService,

    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {provide: ErrorHandler, useClass: IonicErrorHandler}
  ]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts